import { select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { mergeCRFields, objectsMatch } from '@smart-builder/util';
import { APIResult } from '../../callApi';
import {
  ApiActionType,
  BodyState,
  ItemBodyMerger,
  ItemBodyParser,
  ItemSummaryBodyMerger,
  KeyComparer,
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
  RequestBodyFragment,
  RequestBodyFragmentValue,
} from './types';

export function handleError<TItemKey, TItemSummary, TItemBody, TAction>(
  isUpdate: boolean,
  state: MainResourceState<TItemKey, TItemSummary, TItemBody>,
  action: ActionType<TAction>,
): MainResourceState<TItemKey, TItemSummary, TItemBody> {
  const error = (action as any).payload || action;
  console.error(error);

  return {
    ...state,
    pendingLoads: isUpdate ? state.pendingLoads : state.pendingLoads - 1,
    pendingUpdates: isUpdate ? state.pendingUpdates - 1 : state.pendingUpdates,
    error,
    patchData: undefined,
  };
}

export function createErrorHandler<TItemKey, TItemSummary, TItemBody, TAction>(
  isUpdate: boolean,
): MainResourceHandler<TItemKey, TItemSummary, TItemBody, TAction> {
  return (state, action) => handleError(isUpdate, state, action);
}

export function handleBodyLoaded<
  TItemKey,
  TItemSummary,
  TItemBody,
  TItemBodyAPI,
>(
  state: MainResourceState<TItemKey, TItemSummary, TItemBody>,
  response: APIResult,
  frameSeqNumber: number,
  isAnyEditOperation: boolean,
  isCancelOperation: boolean,
  allowMerge: boolean,
  sameKey: KeyComparer<TItemKey, TItemSummary, TItemBody>,
  mergeItemBody: ItemBodyMerger<TItemBody>,
  mergeSummaryBody: ItemSummaryBodyMerger<TItemSummary, TItemBody>,
  parseItemBody: ItemBodyParser<TItemBody, TItemBodyAPI>,
  action: ApiActionType,
): MainResourceState<TItemKey, TItemSummary, TItemBody> {
  const lastBody = state.currentBody;
  const seqNumLast = state.currentRecord?.seqNumCur || 0;
  const seqNumCur = response.seqNumCur || 0;
  const patchData = response.patchData;

  const overrides = state.overrides.flat();

  const newBody = response.result && parseItemBody(response.result);
  const isSameKey = lastBody && sameKey(lastBody, newBody);

  const staleResponse =
    state.frameSeqNumber > frameSeqNumber ||
    (isSameKey && seqNumCur && seqNumLast && seqNumLast > seqNumCur);

  if (staleResponse) {
    // this response is out of date, ignore it
    return {
      ...state,
      pendingLoads: isAnyEditOperation
        ? state.pendingLoads
        : state.pendingLoads - 1,
      pendingUpdates: isAnyEditOperation
        ? state.pendingUpdates - 1
        : state.pendingUpdates,
      patchData: undefined,
    };
  }

  const currentBody =
    isSameKey && allowMerge
      ? mergeItemBody(
          lastBody!,
          mergeCRFields(lastBody, newBody, overrides),
          isAnyEditOperation,
          seqNumLast,
          seqNumCur,
        )
      : mergeCRFields(null, newBody, overrides);

  let newRecordIndex = -1;
  let insertedKeys = state.insertedKeys;

  const records = state.records.map((record, index) => {
    if (!sameKey(record.summary, currentBody)) return record;

    newRecordIndex = index;

    const seqNumPersisted = response.seqNumPersisted || 0;
    const pendingChanges = !isSameKey
      ? []
      : record.pendingChanges
          // remove all pending changes prior to seqNumPersisted
          .filter(c => c.bodySeqNum > seqNumPersisted)
          // remove all onSuccess and onFailure on all pending changes prior to seqNumCurrent
          .map(c =>
            c.bodySeqNum > seqNumCur
              ? c
              : { ...c, onSuccess: undefined, onFailure: undefined },
          );

    // if we have no more pending changes, then our base (seqNumFrom) is the current seqNum
    // if we do have changes, then it is the seqNum prior to our first pending change.
    //    this would typically be equal to seqNumPersisted, but can drift if we've lost
    //    pending changes (e.g. due to the user refreshing).
    const seqNumFrom =
      pendingChanges.length > 0 ? pendingChanges[0].bodySeqNum - 1 : seqNumCur;

    // TODO eventually needs to allow backend to specify the body state too?
    // Currently needs to handle stored structures, which don't exist in database,
    //    but are not modified (and hence need to be in browse state)
    const bodyState = response.modified
      ? response.existsInDatabase
        ? BodyState.edit
        : BodyState.insert
      : BodyState.browse;

    return {
      ...record,
      bodyState,
      summary: mergeSummaryBody(record.summary, currentBody),
      body: currentBody,

      loadedLockNum: response.lockNum || 0,
      seqNumCur,
      seqNumFrom,

      pendingChanges,
    };
  });

  const existingSummary =
    newRecordIndex >= 0 ? state.currentItems[newRecordIndex] : undefined;

  if (
    newRecordIndex < 0 &&
    // If we've got a load multi, then the record should be there unless it was inserted, as the loadMulti
    // is what's driving the loadSingle.
    // If we don't have a loadMulti, then the record will only be there if we add it here ourselves. Technically
    // it's not an insert, but those resources are relying on it, so eh.
    (!state.hasLoadMulti || action === ApiActionType.insert)
  ) {
    insertedKeys = [...insertedKeys, currentBody]; // TODO SM extract key only
    // couldn't find existing summary, create new one

    // Used to assume it's always in insert. Now, instead read from the body
    const bodyState = currentBody.existsInDatabase
      ? currentBody.modified
        ? BodyState.edit
        : BodyState.browse
      : BodyState.insert;

    records.unshift({
      bodyState,
      summary: mergeSummaryBody(undefined, currentBody),
      body: currentBody,

      loadedLockNum: response.lockNum || 0,
      seqNumCur,

      seqNumFrom: response.seqNumCur || 0, // no pending changes, our base is current sequence num, not persisted sequence num
      pendingChanges: [],
    });
    newRecordIndex = 0;
  }

  const newSummary =
    newRecordIndex >= 0 ? records[newRecordIndex].summary : undefined;
  const recordHasChanged = !objectsMatch(existingSummary, newSummary);
  const currentItems = recordHasChanged
    ? records.map(r => r.summary)
    : state.currentItems;

  return {
    ...state,
    pendingLoads: isAnyEditOperation
      ? state.pendingLoads
      : state.pendingLoads - 1,
    pendingUpdates: isAnyEditOperation
      ? state.pendingUpdates - 1
      : state.pendingUpdates,
    cancelCount: isCancelOperation
      ? (state.cancelCount || 0) + 1
      : state.cancelCount,

    error: undefined,
    patchData,

    records,
    lastRecordsChangedAction: action,
    insertedKeys,

    currentRecordIndex: newRecordIndex >= 0 ? newRecordIndex : undefined,
    currentRecord: newRecordIndex >= 0 ? records[newRecordIndex] : undefined,
    currentBody,
    currentItems,

    // Used when registering. We want to allowMerge if
    // we're not doing a fresh load.
    lastLoadParams: state.lastLoadParams && {
      ...state.lastLoadParams,
      allowMerge: true,
    },
  };
}

function mergeArrays<TItem>(destArray?: TItem[], srcArray?: TItem[]) {
  return destArray
    ? srcArray
      ? [...srcArray, ...destArray]
      : destArray
    : srcArray;
}

function mergeNested(
  dest: Record<string, RequestBodyFragmentValue> | undefined,
  source: Record<string, RequestBodyFragmentValue> | undefined,
) {
  if (dest && source) {
    Object.entries(source).forEach(([key, srcValue]) => {
      dest[key] = mergeRequestBodyFragmentValues(dest[key], srcValue);
    });

    return dest;
  } else {
    return dest || source;
  }
}

function mergeRequestBodyFragmentValues(
  destValue: RequestBodyFragmentValue,
  srcValue: RequestBodyFragmentValue,
): RequestBodyFragmentValue {
  let result: RequestBodyFragmentValue = {};
  if (destValue) {
    result.fields = mergeArrays(destValue.fields, srcValue.fields);
    result.fieldsRO = mergeArrays(destValue.fieldsRO, srcValue.fieldsRO);
    result.answers = destValue.answers || srcValue.answers;
    result.dataPacket = destValue.dataPacket || srcValue.dataPacket;
    result.nested = mergeNested(destValue.nested, srcValue.nested);
  } else {
    // dest doesn't already have value. nothing to merge.
    result = { ...srcValue };
  }
  return result;
}

export function mergeRequestBodyFragments(
  dest: RequestBodyFragment,
  source: RequestBodyFragment,
): void {
  Object.entries(source).forEach(([key, srcValue]) => {
    dest[key] = mergeRequestBodyFragmentValues(dest[key], srcValue);
  });
}

export function* buildRequestBody<TItemKey, TItemSummary, TItemBody>(
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  extraFragment?: RequestBodyFragment,
): Generator {
  const { requestBodyFragments } = (yield select(
    selector,
  )) as MainResourceState<TItemKey, TItemSummary, TItemBody>;

  const result: RequestBodyFragment = {};
  requestBodyFragments.forEach(fragment =>
    mergeRequestBodyFragments(result, fragment),
  );
  extraFragment && mergeRequestBodyFragments(result, extraFragment);

  return result;
}

export const initialState: MainResourceState<any, any, any> = {
  frameSeqNumber: 1,
  frameMultiSeqNumber: 0,

  pendingLoads: 0,
  pendingUpdates: 0,
  error: undefined,
  patchData: undefined,

  records: [],
  insertedKeys: [],
  deletedKeys: [],

  currentRecordIndex: undefined,
  currentBody: undefined,
  currentItems: [],

  lastLoadParams: undefined,
  requestBodyFragments: [],
  overrides: [],
  populateOptions: undefined,
};
